import {dateUtils, logging} from "@/utils/utils";
import goalApis from "@/views/GoalSettings/api";
import dayjs from 'dayjs';
import contractsApiInstance from "@/contractsApi";

/**
 * 
 * 기본적으로 목록성 API들은 모두싸인과 프릭스로 분기가 필요함
 * 그리고 서명 기한 변경과 같이 프릭스에서 지원하지 않는 항목들은 모두싸인일 때만 보여주면 됨
 * 화면 list에서 serviceProvider에 따라 프릭스, 모두싸인 분기처리 필요
 * 
 * '영향'의 의미: 일부 API에서 originalContract 내부 값을 직접 참조하는데
 * 모두싸인과 프릭스의 데이터 구조 및 항목이 서로 다름
 * 그리고 이 값들이 아주 폭넓게 사용되고 있어 현재 이 구간을 건드리는 것은 좋지 않아 보임
 * 이 구간들을 점검한 다음 프릭스의 originalContract 값을 변경하는 로직 추가 필요
 * 
 */
export default function ($apiInstance, $store) {
    const goalApi = goalApis($apiInstance, $store)
    return {
        // 영향 인빋음, 완전 다른 도메인
        getManufacturerAccounts: async (success, fail) => {
            return $apiInstance.get(`/accounts/manufacturer?page=1&limit=10000`)
                .then((response) => {
                    success(response);
                }, (error) => {
                    console.log(error);
                    fail(error);
                })
        },
        // 영향 인빋음, 완전 다른 도메인
        getHospitalList: async (params, success, fail) => {
            $store.commit('setIsLoading', true);
            return $apiInstance.post(`/companies/list/v2`, params)
                .then((response) => {
                    success(response);
                }, (error) => {
                    console.log(error);
                    fail(error);
                }).finally(() => $store.commit('setIsLoading', false));
        },
        // 영향 인빋음, 완전 다른 도메인
        getDistinctDeptName: async (params, success, fail) => {
            $store.commit('setIsLoading', true);
            try {
                const response = await $apiInstance.post(`/companies/distinct/deptName`, params);

                const deptNames = response.data
                    .filter(item => item.includes("지점") || item.includes("영업소") || item === "종합병원팀")
                    .map(item => ({label: item, value: item}));

                success(deptNames);
            } catch (error) {
                console.log(error);
                fail(error);
            } finally {
                $store.commit('setIsLoading', false);
            }
        },
        // /contracts/templates 이거도 있음
        
        // 영향 받음, getContractById(컨트롤러, 서비스 함수명), metadata 쪽을 사용함. 프릭스 쪽 API 새로 따야 함(계약서 dto별로 점진적 적용)
        // 고려해야 하는건, 서비스 함수 내부에서 하나 빼고 모두 isOriginalDocs가 true이고 화면에서는 모두 false로 조회함
        // 따라서, isOriginalDocs가 꼭 true인 상태로 받아와야 하는지 고려 필요
        getContractTemplate: async (params, success, fail) => {
            $store.commit('setIsLoading', true);
            return $apiInstance.get(`/contracts/${params}`)
                .then((response) => {
                    success(response);
                }, (error) => {
                    console.log(error);
                    fail(error);
                }).finally(() => $store.commit('setIsLoading', false));
        },

        // 영향 받음, 전체적으로 값 바인딩 확인 필요, 프릭스 api 처리방식대로 처리는 하되 구독정보나 ceo 정보 등에 대한 사항은 로직 추가해야 됨
        createContract: async (params, success, fail) => {
            $store.commit('setIsLoading', true);
            return $apiInstance.post(`/contracts/create-by-template`, params)
                .then((response) => {
                    success(response);
                }, (error) => {
                    console.log(error);
                    fail(error);
                }).finally(() => $store.commit('setIsLoading', false));
        },

        // 사용하지 않을 예정, 사유: 프릭스에서는 이 과정이 없음. 단, 프릭스에서 secure-link 개발 완료하게 되면 사용하게 될 수도 있음
        createContractDraft: async (params, success, fail) => {
            return $apiInstance.post(`/contracts/embedded-drafts-by-template`, params)
                .then((response) => {
                    success(response);
                }, (error) => {
                    console.log(error);
                    fail(error);
                })
        },

        // 영향 받음, 그런데 isOriginalDocs는 사용하지 않고 DB값만 써야 함(메타데이터는 모두싸인 응답값에만 있기 때문)
        // 그 외에 originalDocs가 true일 때 어떤 처리가 되는지 조사 필요
        getContract: async (contractId, isOriginalDocs, success, fail) => {
            $store.commit('setIsLoading', true);
            return $apiInstance.get(`/contracts/${contractId}?isOriginalDocs=${isOriginalDocs}`)
                .then((response) => {
                    logging().exceptProd('contract ==>', response.data)
                    success(response);
                }, (error) => {
                    fail(error);
                }).finally(() => $store.commit('setIsLoading', false));
        },

        // 영향 받음, originalContract 직접 참조해서 처리하는 부분이 있음
        getContracts: async (params, success, fail) => {
            $store.commit('setIsLoading', true);
            return $apiInstance.post(`/contracts`, params)
                .then((response) => {
                    logging().exceptProd('contracts ==>', response.data)
                    response.data.docs = response.data.docs.map(d => {
                        return {isChecked: false, ...d}
                    })
                    success(response);
                }, (error) => {
                    fail(error);
                }).finally(() => $store.commit('setIsLoading', false));
        },

        // 프릭스일 때랑 아닐 때 로직분기 필요, 프릭스에서 계약 상태에 따라 새 창에서 열리는게 있어서 확인해봐야 할 듯
        getContractsPdfDownload: async (params, success, fail) => {
            $store.commit('setIsLoading', true);
            return $apiInstance.post(`/contracts/pdf-download`, params)
                .then((response) => {
                    logging().exceptProd('getContractsPdfDownload ==>', response.data)
                    success(response);
                }, (error) => {
                    fail(error);
                }).finally(() => $store.commit('setIsLoading', false));
        },

        // 목록 내부에서 분기 필요, 프릭스일 때는 내부 API 이용해서 알림톡 전송해야 함
        // 모두싸인은 모두싸인 API로 요청하고 바로 응답 반환
        resendRequest: async (params, success, fail) => {
            $store.commit('setIsLoading', true);
            return $apiInstance.post(`/contracts/${params}/resend-sign-request`)
                .then((response) => {
                    logging().exceptProd('resend request ==>', response.data)
                    success(response);
                }, (error) => {
                    fail(error);
                }).finally(() => $store.commit('setIsLoading', false));
        },

        // 현재 프릭스에서 구현되지 않은 부분, 현실에서 거의 사용되지 않고 있기 때문에
        // 프릭스일 때 숨김처리 하면 될 듯. 따로 작업할 필요 없음
        changeSigningDue: async (params, success, fail) => {
            $store.commit('setIsLoading', true);
            const {contractId, date} = params;
            return $apiInstance.put(`/contracts/${contractId}/change-signing-due`, {
                date: date,
            })
                .then((response) => {
                    logging().exceptProd('signing due ==>', response.data)
                    success(response);
                }, (error) => {
                    fail(error);
                }).finally(() => $store.commit('setIsLoading', false));
        },

        // 목록 내부에서 분기 필요(PRIX에는 없는 기능)
        // status 수동으로 업데이트 하는 API를 만들고
        // 전자계약 모바일 화면에서 status로 검증하는거 추가
        cancelContract: (contractId, success, fail) => {
            $store.commit('setIsLoading', true);
            return $apiInstance.post(`/contracts/${contractId}/cancel`, {
                message: "서명요청 취소",
                accessibleByParticipant: false
            })
                .then((response) => {
                    logging().exceptProd('resend request ==>', response.data)
                    success(response);
                }, (error) => {
                    fail(error);
                }).finally(() => $store.commit('setIsLoading', false));
        },

        // 목록 내부에서 분기 필요
        // 프릭스와 모두싸인의 차이는
        // 모두싸인: 전화번호 인증 후 자동 다운로드
        // 프릭스: 전화번호 인증 후 직접 다운로드
        sendCompleteContract: (param, success, fail) => {
            $store.commit('setIsLoading', true);
            return $apiInstance.post(`/contracts/${param?.contractId}/send-complete-contract`, [
                ...param?.contactList
            ])
                .then((response) => {
                    logging().exceptProd('resend request ==>', response.data)
                    success(response);
                }, (error) => {
                    fail(error);
                }).finally(() => $store.commit('setIsLoading', false));
        },

        // 목록 내부 분기 필요
        // 데스크톱에서는 알림톡을 보내지 않고 바로 띄우는 기능
        // 모바일에서는 알림톡으로 진입 시 이 페이지로 진입함
        // isWeb 파라미터로 구분하며, 리디렉션 기능 추가되면 모두싸인 전자계약의 리디렉션 URL대로 하면 됨
        sendSecureSignLink: async (params, success, fail) => {
            $store.commit('setIsLoading', true);
            return $apiInstance.post(`/contracts/secure-sign-link`, params)
                .then((response) => {
                    success(response);
                }, (error) => {
                    logging().exceptProd(error);
                    fail(error);
                }).finally(() => $store.commit('setIsLoading', false));
        },

        // API 내부에서 분기 필요
        // deleteYn을 하고 수동으로 상태 업데이트 하여 모바일에서도 확인할 수 없도록 해야 함
        deleteContracts: async (contractIds, success, fail) => {
            $store.commit('setIsLoading', true); // 로딩 상태 시작
            return $apiInstance.delete('/contracts', {data: {contractIds}})
                .then((response) => {
                    success(response); // 성공 콜백 함수 호출
                })
                .catch((error) => {
                    fail(error); // 실패 콜백 함수 호출
                })
                .finally(() => {
                    $store.commit('setIsLoading', false); // 로딩 상태 종료
                });
        },
        
        // 위에서 사용된 contractService 내부의 getContracts를 수정하면 됨
        fetchContractsExcel: async (params, success, fail) => {
            $store.commit('setIsLoading', true);
            return $apiInstance.post('/contracts/excel', {...params, limit: 100000000}, { // limit 에 충분히 큰 수를 부여해서 선택한 기간에 모든 데이터를 다운로드 받을 수 있도록 처리
                responseType: 'blob',
                headers: {}
            })
                .then(response => {
                    const url = window.URL.createObjectURL(new Blob([response.data]));
                    const link = document.createElement('a');
                    link.href = url;
                    const formattedDate = dayjs().format('YYYYMMDD');
                    link.setAttribute('download', `전자계약목록_${formattedDate}.xlsx`);
                    document.body.appendChild(link);
                    link.click();

                    window.URL.revokeObjectURL(url);
                    link.parentNode.removeChild(link);
                    success(response);
                })
                .catch(error => {
                    console.error('Error:', error);
                    fail(error);
                })
                .finally(() => {
                    $store.commit('setIsLoading', false);
                });
        },

        // 영향 받음, 메타데이터 업데이트하는 부분이 있음
        syncClient: async (params, success, fail) => {
            return $apiInstance.patch(`/contracts/sync/hospital`, params)
                .then((response) => {
                    success(response);
                }, (error) => {
                    console.log(error);
                    fail(error);
                })
        },

        // 영향 받음, 마찬가지로 originalContract를 검색 필터로 사용함
        dashboard: async (params, success, fail) => {
            const {startDate, endDate, year} = params
            $store.commit('setIsLoading', true);

            let url = '/contracts/goals/dashboard?';
            if (startDate && endDate) {
                url += `startDate=${startDate}&endDate=${endDate}`;
            }
            if (year) {
                url += `&year=${year}`;
            }

            // 2026-08-29: 목표실적 대시보드는 contracts-api 로 이관됨(inapi 전자계약 모듈 제거).
            // 이 화면(GoalManagement)만 $apiInstance(inapi) 대신 contracts-api 인스턴스를 쓴다.
            // 경로·쿼리·응답 형태는 동일. (intest 는 contracts-api 환경이 없어 로컬 터널 주소가 잡힌다)
            return contractsApiInstance.get(url)
                .then((response) => {
                    logging().exceptProd('goals dashboard==>', response.data)
                    success(response);
                }, (error) => {
                    fail(error);
                }).finally(() => $store.commit('setIsLoading', false));
        },

        // 목표실적 v2 — 월×지점×그룹×상태 큐브를 한 번에 받는다.
        // 필터(기간·지점·그룹·산입기준)는 화면이 큐브를 접어서 적용하므로 파라미터는 year 하나뿐이고,
        // 필터를 바꿀 때 재조회하지 않는다. v1(dashboard)과 병존한다 — v1 화면이 아직 그 경로를 쓴다.
        dashboardV2: async (params, success, fail) => {
            const {year} = params
            $store.commit('setIsLoading', true);

            return contractsApiInstance.get(`/contracts/goals/v2/dashboard?year=${year}`)
                .then((response) => {
                    logging().exceptProd('goals v2 dashboard==>', response.data)
                    success(response);
                }, (error) => {
                    fail(error);
                }).finally(() => $store.commit('setIsLoading', false));
        },
        getActiLinkProducts: async (success, fail) => {
            try {
                const response = await $apiInstance.get(`/products/actilink/reborns`);
                if (response.status !== 200 && response.status !== 201)
                    throw Error('서버에서 응답을 받지 못헀습니다.');
                success(response);
            } catch (error) {
                fail(error);
            }
        },
        authorizationCard: async (param, success, fail) => {
            return $apiInstance.post(`/contracts/check/card`, {
                cNo: param.cNo,
                cardNo: param.cardNo,
                expiration: param.expiration,
                identificationNo: param.identificationNo
            })
                .then((response) => {
                    success(response);
                }, (error) => {
                    fail(error);
                })
        },
        getTempContract: async (params, success, fail) => {
            // $store.commit('setIsLoading', true);
            const {contractType} = params
            return $apiInstance.get(`/contracts/temp/${contractType}`)
                .then((response) => {
                    logging().exceptProd('get temp contract ==>', response.data)
                    success(response.data);
                }, (error) => {
                    fail(error);
                }).finally(() => $store.commit('setIsLoading', false));
        },
        createTempContract: async (params, success, fail) => {
            $store.commit('setIsLoading', true);
            return $apiInstance.post(`/contracts/temp`, params)
                .then((response) => {
                    logging().exceptProd('create temp contract ==>', response.data)
                    success(response);
                }, (error) => {
                    fail(error);
                }).finally(() => $store.commit('setIsLoading', false));
        },
        ...goalApi,
    }
}